"use client";

import type React from "react";
import { useEffect, useState, useRef } from "react";
import { AppLayout } from "src/components/layouts";
import AdminService from "src/services/admin.services";
import BackButton from "src/components/BackButton";
import SearchInput from "src/components/ui/SearchInput";
import UserTable from "./components/UserTable";
import { QueryFunctionContext, useQuery } from "react-query";
import { NIGERIAN_STATES } from "../../utils/constants";

interface User {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  is_suspended: boolean;
  last_login?: string;
  phone?: string;
}

const adminService = new AdminService();
const ITEMS_PER_PAGE = 100;
const fetchUsers = async (context: QueryFunctionContext) => {
  const [_key, currentPage, itemsPerPage] = context.queryKey;

  const users = await adminService.getUsers(
    currentPage as number,
    (itemsPerPage as number).toString()
  );

  if (!Array.isArray(users)) {
    throw new Error("Unexpected response format");
  }

  return users;
};

const UserManagement: React.FC = () => {
  const [data, setData] = useState<User[]>([]);
  const [filteredData, setFilteredData] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // const [isLoading, setIsLoading] = useState<boolean>(true); // Unused
  // const [error, setError] = useState<string | null>(null); // Unused
  const [totalPages, setTotalPages] = useState<number>(10);
  const [searching, setSearching] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("all");

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: users,
    isLoading,
    isError,
    error,
  } = useQuery(["users", currentPage, ITEMS_PER_PAGE], fetchUsers, {
    keepPreviousData: true,
    enabled: !!currentPage,
    refetchInterval: 60000,
    refetchOnWindowFocus: false,

    onSuccess: (users) => {
      setData(users);
      if (!searching) setFilteredData(users);
      if (users.length < ITEMS_PER_PAGE) {
        setTotalPages(currentPage);
      } else {
        setTotalPages(currentPage + 1);
      }
    },
    onError: () => {
      // Error fetching users - silently fail
    },
  });

  // useEffect(() => {
  //   fetchData(); // Initial fetch

  //   let interval: NodeJS.Timeout | undefined;

  //   if (!searching) {
  //     interval = setInterval(fetchData, 60000); // 60 seconds
  //   }

  //   return () => {
  //     if (interval) clearInterval(interval);
  //   };
  // }, [currentPage, searching]);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      let filtered = data;

      // Apply state filter (if users have location data)
      if (selectedState !== "all") {
        // Note: Users might not have location data, so this is a placeholder
        // You would need to add location/address field to User interface
        filtered = filtered.filter((user) => {
          // For now, we'll skip state filtering for users since they don't have location data
          // This can be implemented when user location data is available
          return true;
        });
      }

      // Apply search filter - search by first name or last name
      if (searchTerm.trim()) {
        setSearching(true);
        filtered = filtered.filter(
          (user) =>
            user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      } else {
        setSearching(false);
      }

      setFilteredData(filtered);
    }, 300); // Debounce delay

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, data, selectedState]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleDownload = () => {
    const csvContent = [
      ["S/N", "First Name", "Last Name", "Email", "Status", "Last Login"],
      ...filteredData.map((user, index) => [
        index + 1,
        user.first_name,
        user.last_name,
        user.email,
        user.is_suspended ? "Suspended" : "Active",
        user.last_login || "N/A",
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "user_table.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AppLayout>
      <div className="w-full px-2 py-4 sm:px-3 lg:px-4">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            {/* Header Section */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
              {/* Title & Back Button */}
              <div className="flex items-center gap-3">
                <BackButton />
                <h2 className="text-xl sm:text-2xl font-bold text-amber-900">
                  User Management
                </h2>
              </div>

              {/* Search Input */}
              <div className="w-full md:w-80">
                <SearchInput
                  search={handleSearch}
                  placeholder="Search by first or last name..."
                />
              </div>
            </div>

            {/* Error Message or Table */}
            {error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg
                      className="h-5 w-5 text-red-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      {(error as Error).message}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <UserTable
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                setCurrentPage={setCurrentPage}
                itemsPerPage={ITEMS_PER_PAGE}
                filteredData={filteredData}
                loading={isLoading}
                onDownload={handleDownload}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default UserManagement;
